import React, { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import { Toaster, toast } from 'react-hot-toast';
import { BuildingOfficeIcon, EyeIcon, XMarkIcon } from '@heroicons/react/24/solid';
import * as XLSX from 'xlsx';
import { getEstatusEquipos } from '../../services/Reportes.js';
import { formatDate } from '../../formatters/date';

const customStyles = {
  table: {
    style: {
      backgroundColor: 'white',
      borderRadius: '8px',
    },
  },
  head: {
    style: {
      fontSize: '14px',
      fontWeight: 'bold',
      color: '#2F336A',
      backgroundColor: '#F2F5FE',
      borderBottom: '1px solid #D9E1F2',
    },
  },
  headRow: {
    style: {
      minHeight: '48px',
      borderBottomWidth: '1px',
    },
  },
  headCells: {
    style: {
      paddingLeft: '12px',
      paddingRight: '12px',
      whiteSpace: 'nowrap',
    },
  },
  rows: {
    style: {
      fontSize: '13px',
      backgroundColor: 'white',
      minHeight: '52px',
      borderBottom: '1px solid #F3F4F6',
      transition: 'background-color 0.2s ease-in-out',
    },
    highlightOnHoverStyle: {
      backgroundColor: '#F9FAFB',
      borderBottomColor: '#E5E7EB',
      outline: 'none',
    },
  },
  cells: {
    style: {
      paddingLeft: '12px',
      paddingRight: '12px',
    },
  },
};

const estadoBadge = (val) => {
  const v = (val || '').toLowerCase();
  if (v === 'ok' || v === 'aprobado' || v === 'aprobada' || v === 'en obra') return 'bg-green-100 text-green-700';
  if (v === 'pendiente') return 'bg-yellow-100 text-yellow-700';
  if (v === 'rechazado' || v === 'rechazada') return 'bg-red-100 text-red-700';
  if (v === 'en proceso') return 'bg-blue-100 text-blue-700';
  if (v === 'salida de obra') return 'bg-orange-100 text-orange-700';
  return 'bg-gray-100 text-gray-700';
};

const badgeAcred = (estado) => {
  if (!estado) return 'bg-gray-100 text-gray-500';
  const v = estado.toLowerCase();
  if (v === 'aprobada') return 'bg-green-100 text-green-700';
  if (v === 'rechazada') return 'bg-red-100 text-red-700';
  if (v === 'pendiente') return 'bg-yellow-100 text-yellow-700';
  return 'bg-gray-100 text-gray-700';
};

const ModalAcreditacion = ({ acred, onCerrar }) => {
  if (!acred) return null;

  const formatFecha = (f) => {
    if (!f) return '—';
    try {
      const d = f instanceof Date ? f : new Date(f);
      return isNaN(d) ? '—' : `${String(d.getUTCDate()).padStart(2,'0')}/${String(d.getUTCMonth()+1).padStart(2,'0')}/${d.getUTCFullYear()}`;
    } catch { return '—'; }
  };

  const estadoChip = (est) => {
    const base = 'text-[10px] px-2 py-0.5 rounded-full font-medium';
    if (est === 'Aprobado') return `${base} bg-green-100 text-green-700`;
    if (est === 'Rechazado') return `${base} bg-red-100 text-red-700`;
    return `${base} bg-yellow-100 text-yellow-700`;
  };

  const checklist = acred.tecnica?.checklist;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center px-5 py-3 border-b">
          <div className="flex items-center gap-3">
            <p className="font-semibold text-gray-800">Detalle Acreditación</p>
            {acred.estado && (
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${badgeAcred(acred.estado)}`}>
                {acred.estado}
              </span>
            )}
          </div>
          <button type="button" onClick={onCerrar} className="text-gray-400 hover:text-red-500 p-1 rounded-full hover:bg-gray-100">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto p-5 space-y-5">
          {/* Técnica */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Acreditación Técnica</p>
            {checklist ? (
              <div className={`rounded-lg p-3 border text-xs ${checklist.estado === 'Rechazado' ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-gray-50'}`}>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className={estadoChip(checklist.estado)}>{checklist.estado || 'Pendiente'}</span>
                </div>
                {checklist.estado === 'Rechazado' && checklist.motivos?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2 pl-1">
                    {checklist.motivos.map(m => (
                      <span key={m} className="text-[10px] bg-red-100 text-red-700 px-2 py-0.5 rounded-full">{m}</span>
                    ))}
                  </div>
                )}
                {checklist.estado === 'Rechazado' && checklist.comentario && (
                  <p className="text-red-600 italic mt-1 pl-1">{checklist.comentario}</p>
                )}
              </div>
            ) : (
              <p className="text-xs text-gray-400 italic">Sin evaluación técnica.</p>
            )}
          </div>

          {/* Documentos Logística */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Documentos Logística</p>
            {(() => {
              const docsLogPendRech = (acred.documentosLogistica || []).filter(d => d.estado !== 'Aprobado');
              return docsLogPendRech.length > 0 ? (
                <table className="w-full border-collapse border border-gray-200 text-xs">
                  <thead>
                    <tr className="bg-gray-100 text-left text-gray-500 uppercase">
                      <th className="p-2 border font-semibold">Documento</th>
                      <th className="p-2 border font-semibold w-28">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {docsLogPendRech.map((doc, i) => (
                      <tr key={i} className={`border ${doc.estado === 'Rechazado' ? 'bg-red-50' : ''}`}>
                        <td className="p-2 border text-gray-700">
                          {doc.documentoGeneral}
                          {doc.estado === 'Rechazado' && doc.comentario && (
                            <p className="text-red-600 italic text-[10px] mt-0.5">⚠ {doc.comentario}</p>
                          )}
                        </td>
                        <td className="p-2 border">
                          <span className={estadoChip(doc.estado)}>{doc.estado || 'Pendiente'}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-xs text-gray-400 italic">No hay documentos rechazados.</p>
              );
            })()}
          </div>

          {/* Documentos Obra */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Documentos de Obra</p>
            {(acred.documentosObra || []).length > 0 ? (
              <table className="w-full border-collapse border border-gray-200 text-xs">
                <thead>
                  <tr className="bg-gray-100 text-left text-gray-500 uppercase">
                    <th className="p-2 border font-semibold">Documento</th>
                    <th className="p-2 border font-semibold">Fecha Emisión</th>
                    <th className="p-2 border font-semibold">Fecha Vencimiento</th>
                  </tr>
                </thead>
                <tbody>
                  {acred.documentosObra.map((doc, i) => (
                    <tr key={i} className="border">
                      <td className="p-2 border text-gray-700">{doc.documentoGeneral}</td>
                      <td className="p-2 border text-gray-600">{formatFecha(doc.fechaVigencia)}</td>
                      <td className="p-2 border text-gray-600">{formatFecha(doc.fechaVencimiento)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-xs text-gray-400 italic">Sin documentos de obra.</p>
            )}
          </div>

          {/* Historial */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Historial</p>
            {acred.historial?.length > 0 ? (
              <table className="w-full border-collapse border border-gray-200 text-xs">
                <thead>
                  <tr className="bg-gray-100 text-left text-gray-500 uppercase">
                    <th className="p-2 border font-semibold">Fecha</th>
                    <th className="p-2 border font-semibold">Acción</th>
                    <th className="p-2 border font-semibold">Usuario</th>
                  </tr>
                </thead>
                <tbody>
                  {acred.historial.map((h, i) => (
                    <tr key={i} className="border hover:bg-gray-50">
                      <td className="p-2 border text-gray-500 whitespace-nowrap">{formatFecha(h.fecha)}</td>
                      <td className="p-2 border text-gray-700">{h.accion}</td>
                      <td className="p-2 border text-gray-700">{h.nombre || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-xs text-gray-400 italic">Sin historial.</p>
            )}
          </div>
        </div>

        <div className="flex justify-end px-5 py-3 border-t">
          <button type="button" onClick={onCerrar}
            className="text-sm px-5 py-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 transition">
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

const EstatusEquipos = () => {
  const [data, setData] = useState([]);
  const [filtroTexto, setFiltroTexto] = useState('');
  const [filtroCentro, setFiltroCentro] = useState('');
  const [filtroEstatus, setFiltroEstatus] = useState('ALL');
  const [acredModal, setAcredModal] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const res = await getEstatusEquipos();
        setData(Array.isArray(res) ? res : []);
      } catch {
        toast.error('No se pudo cargar el estatus de equipos');
      }
    };
    loadData();
  }, []);

  const filteredData = data.filter(row => {
    const q = filtroTexto.toLowerCase();
    const matchTexto =
      !q ||
      (row.codigoEquipo || '').toLowerCase().includes(q) ||
      (row.centroCosto || '').toLowerCase().includes(q);

    const matchCentro =
      !filtroCentro ||
      (row.centroCosto || '').toLowerCase().includes(filtroCentro.toLowerCase());

    const matchEstatus =
      filtroEstatus === 'ALL' || row.estadoEquipo === filtroEstatus;

    return matchTexto && matchCentro && matchEstatus;
  });

  const handleDescargarExcel = () => {
    const exportData = filteredData.map(row => ({
      'N° Solicitud':            row.numeroSolicitud,
      'Fecha Solicitud':         row.fechaCreacionSolicitud || '—',
      'Fecha Aprobación':        row.fechaAprobacion,
      'Fecha Acuerdo PLE':       row.fechaAcuerdoPLE || '—',
      'Codigo Equipo':           row.codigoEquipo,
      'Centro de costo':         row.centroCosto,
      'Carpeta':                 row.carpeta,
      'Calidad':                 row.estadoImplementa === 'Finalizado' ? 'Aprobado' : row.estadoImplementa,
      'Fecha Calidad':           row.fechaCalidad || '—',
      'Fecha Presentación':      row.fechaPresentacion || '—',
      'Acred. Técnica':          row.acreditacionTecnica || '—',
      'Acred. Documental':       row.acreditacionDocumental || '—',
      'Acreditación':            row.acreditacion?.estado || '—',
      'Fecha Acreditación':      row.fechaAcreditacion || '—',
      'Estatus':                 row.estadoEquipo,
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Estatus Equipos');
    XLSX.writeFile(wb, 'estatus_equipos.xlsx');
  };

  const columns = [
    {
      name: 'N° Solicitud',
      width: '130px',
      selector: row => row.numeroSolicitud,
      sortable: true,
    },
    {
      name: 'Fecha Solicitud',
      width: '160px',
      selector: row => row.fechaCreacionSolicitud || '',
      sortable: true,
      cell: (row) => (
        <span className="text-xs text-gray-600">{row.fechaCreacionSolicitud || '—'}</span>
      ),
    },
    {
      name: 'Fecha Aprobación',
      width: '160px',
      selector: row => row.fechaAprobacion,
      sortable: true,
      cell: (row) => (
        <span className="text-xs text-gray-600">{row.fechaAprobacion}</span>
      ),
    },
    {
      name: 'Fecha Acuerdo PLE',
      width: '160px',
      selector: row => row.fechaAcuerdoPLE || '',
      sortable: true,
      cell: (row) => (
        <span className="text-xs text-gray-600">{row.fechaAcuerdoPLE || '—'}</span>
      ),
    },
    {
      name: 'Codigo Equipo',
      width: '150px',
      selector: row => row.codigoEquipo,
      sortable: true,
    },
    {
      name: 'Centro de costo',
      width: '160px',
      selector: row => row.centroCosto,
      sortable: true,
      cell: (row) => (
        <div className="flex items-center gap-2">
          <BuildingOfficeIcon className="w-4 h-4 text-blue-500 shrink-0" />
          <span>{row.centroCosto}</span>
        </div>
      ),
    },
    {
      name: 'Carpeta',
      width: '140px',
      selector: row => row.carpeta,
      sortable: true,
      cell: (row) => (
        <div className={`text-xs px-2 py-1 rounded-full text-center ${estadoBadge(row.carpeta)}`}>
          {row.carpeta}
        </div>
      ),
    },
    {
      name: 'Calidad',
      width: '160px',
      selector: row => row.estadoImplementa,
      sortable: true,
      cell: (row) => {
        const label = row.estadoImplementa === 'Finalizado' ? 'Aprobado' : row.estadoImplementa;
        return (
          <div className={`text-xs px-2 py-1 rounded-full text-center ${estadoBadge(label)}`}>
            {label}
          </div>
        );
      },
    },
    {
      name: 'Fecha Calidad',
      width: '150px',
      selector: row => row.fechaCalidad || '',
      sortable: true,
      cell: (row) => (
        <span className="text-xs text-gray-600">{row.fechaCalidad || '—'}</span>
      ),
    },
    {
      name: 'Fecha Presentación',
      width: '150px',
      selector: row => row.fechaPresentacion || '',
      sortable: true,
      cell: (row) => (
        <span className="text-xs text-gray-600">{row.fechaPresentacion || '—'}</span>
      ),
    },
    {
      name: 'Acred. Técnica',
      width: '150px',
      selector: row => row.acreditacionTecnica || '',
      sortable: true,
      cell: (row) => (
        <div className={`text-xs px-2 py-1 rounded-full text-center ${estadoBadge(row.acreditacionTecnica)}`}>
          {row.acreditacionTecnica || '—'}
        </div>
      ),
    },
    {
      name: 'Acred. Documental',
      width: '160px',
      selector: row => row.acreditacionDocumental || '',
      sortable: true,
      cell: (row) => (
        <div className={`text-xs px-2 py-1 rounded-full text-center ${estadoBadge(row.acreditacionDocumental)}`}>
          {row.acreditacionDocumental || '—'}
        </div>
      ),
    },
    {
      name: 'Acreditación',
      width: '170px',
      selector: row => row.acreditacion?.estado || '',
      sortable: true,
      cell: (row) => (
        <div className="flex items-center gap-2">
          {row.acreditacion ? (
            <>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${badgeAcred(row.acreditacion.estado)}`}>
                {row.acreditacion.estado}
              </span>
              <button
                type="button"
                title="Ver detalle"
                onClick={() => setAcredModal(row.acreditacion)}
                className="p-1 bg-indigo-500 text-white rounded hover:bg-indigo-600"
              >
                <EyeIcon className="w-3.5 h-3.5" />
              </button>
            </>
          ) : (
            <span className="text-xs text-gray-400 italic">Sin acreditación</span>
          )}
        </div>
      ),
    },
    {
      name: 'Fecha Acreditación',
      width: '160px',
      selector: row => row.fechaAcreditacion || '',
      sortable: true,
      cell: (row) => (
        <span className="text-xs text-gray-600">{row.fechaAcreditacion || '—'}</span>
      ),
    },
    {
      name: 'Estatus',
      width: '140px',
      selector: row => row.estadoEquipo,
      sortable: true,
      cell: (row) => (
        <div className={`text-xs px-2 py-1 rounded-full text-center ${estadoBadge(row.estadoEquipo)}`}>
          {row.estadoEquipo}
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="mx-4 lg:mx-12 mt-4">

        {/* Header */}
        <div className="flex justify-between items-center px-4 py-2 border-b border-gray-200 bg-white rounded-t-md shadow-sm">
          <div className="flex items-center gap-2">
            <div className="w-2.5 h-2.5 rounded-full bg-blue-500 animate-pulse" />
            <p className="text-gray-800 font-semibold text-base md:text-lg">
              Estatus Equipos
            </p>
          </div>
        </div>

        {/* Filtros */}
        <div className="bg-white p-6 rounded-lg shadow-lg mt-2">
          <div className="flex justify-start items-center p-2 md:ml-6 md:mr-6 relative top-1">
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mt-2">

              <div className="flex flex-col">
                <label className="text-xs font-semibold mb-1 whitespace-nowrap">Codigo Equipo</label>
                <input
                  type="text"
                  className="text-xs border border-gray-300 p-2 rounded w-full"
                  placeholder="Ej: RC-029"
                  value={filtroTexto}
                  onChange={e => setFiltroTexto(e.target.value)}
                />
              </div>

              <div className="flex flex-col">
                <label className="text-xs font-semibold mb-1 whitespace-nowrap">Centro de costo</label>
                <input
                  type="text"
                  className="text-xs border border-gray-300 p-2 rounded w-full"
                  placeholder="Ej: 342"
                  value={filtroCentro}
                  onChange={e => setFiltroCentro(e.target.value)}
                />
              </div>

              <div className="flex flex-col">
                <label className="text-xs font-semibold mb-1">Estatus</label>
                <select
                  className="text-xs border border-gray-300 p-2 rounded"
                  value={filtroEstatus}
                  onChange={e => setFiltroEstatus(e.target.value)}
                >
                  <option value="ALL">Todos</option>
                  <option value="Pendiente">Pendiente</option>
                  <option value="En obra">En obra</option>
                  <option value="Salida de Obra">Salida de Obra</option>
                </select>
              </div>

            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 w-full mt-4 md:ml-8">
            <div className="flex items-end">
              <button
                type="button"
                onClick={handleDescargarExcel}
                className="underline text-green-800 hover:text-green-900 whitespace-nowrap"
              >
                Descargar Excel Estatus Equipos.
              </button>
            </div>
          </div>
        </div>

        {/* Tabla */}
        <div className="bg-white p-6 rounded-lg shadow-lg items-end mt-4">
          <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
            <div className="flex flex-col">
              <DataTable
                columns={columns}
                data={filteredData}
                highlightOnHover
                pagination
                striped
                className="border border-gray-300 rounded-lg shadow-sm"
                noHeader
                selectableRowsHighlight
                customStyles={customStyles}
                noDataComponent="No hay equipos registrados"
              />
            </div>
          </div>
        </div>

      </div>

      {acredModal && <ModalAcreditacion acred={acredModal} onCerrar={() => setAcredModal(null)} />}

      <Toaster
        position="top-right"
        toastOptions={{ style: { zIndex: 9999 } }}
      />
    </>
  );
};

export default EstatusEquipos;
